// ─── Tracking Layer v2 — Core Service ───
// Serviço central de tracking com adapter registry, contexto automático e debug mode.
// Exposto como window.__tracking para consumo global.
// Não depende de nenhuma estrutura legada.
//
// Este arquivo é um ES module: NÃO se auto-executa. É bundlado por esbuild a
// partir de `tracking-bundle.ts`, que injeta os adapters e chama bootstrapCore()
// num único bloco síncrono (create -> registra adapters -> drena buffer). Isso
// elimina a corrida de ordem de carregamento que existia quando core e adapters
// eram scripts separados: quando bootstrapCore retorna, os adapters já estão no
// registry e `ready === true` significa de fato "adapters registrados".

import { createProductEventCollector } from "./tracking-product-events";

// Teto de seguranca para a fila de eventos pendentes. So e exercitada se nenhum
// adapter for passado no bootstrap (caminho anormal). Evita vazamento de memoria.
const MAX_PENDING_EVENTS = 50;

/**
 * Inicializa o core de tracking de forma determinística e síncrona.
 *
 * @param initialAdapters Adapters a registrar ANTES de drenar o buffer, de modo
 *   que nenhum evento (nem os bufferizados pelo stub inline) caia num intervalo
 *   sem adapter. Adapters adicionais ainda podem ser registrados depois via
 *   `window.__tracking.registerAdapter` (ex: Rudder quando o SDK carregar).
 * @returns a instância do serviço (também exposta em `window.__tracking`).
 */
export function bootstrapCore(initialAdapters: TrackingAdapter[] = []): TrackingServiceAPI {
  const STORAGE_KEY = "__tracking_debug";

  function isDebugEnabled(): boolean {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }

  function log(label: string, ...args: unknown[]): void {
    if (!isDebugEnabled() && !service.debug) return;
    console.debug(`%c[Tracking][${label}]`, "color: #6C5CE7; font-weight: bold;", ...args);
  }

  function warn(label: string, ...args: unknown[]): void {
    console.warn(`[Tracking][${label}]`, ...args);
  }

  // ─── Default context provider ───

  function defaultContextProvider(): TrackingEventContext {
    return {};
  }

  // ─── Service singleton ───

  const adapters = new Map<string, TrackingAdapter>();
  let contextProvider: () => Partial<TrackingEventContext> = defaultContextProvider;
  let ready = false;
  const readyCallbacks: Array<() => void> = [];

  // Rede de seguranca: eventos disparados antes de qualquer adapter existir ficam
  // aqui em vez de serem descartados, e sao liberados quando o primeiro adapter
  // registra. No caminho normal (bootstrap injeta os adapters antes de drenar)
  // esta fila nunca acumula nada.
  const pendingEvents: TrackingEvent[] = [];

  function dispatchToAdapters(event: TrackingEvent): void {
    adapters.forEach((adapter) => {
      try {
        adapter.send(event);
        log("adapter:" + adapter.name, "sent", event.name);
      } catch (err) {
        warn("adapter:" + adapter.name, "failed to send", event.name, err);
      }
    });
  }

  function runReadyCallback(cb: () => void): void {
    try {
      cb();
    } catch (err) {
      warn("onReady", "callback failed", err);
    }
  }

  const service = {
    debug: false,

    trackProductEvent: createProductEventCollector((name, payload) => service.trackEvent(name, payload)),

    trackEvent(name: string, payload: Record<string, unknown>): void {
      if (!name) {
        warn("trackEvent", "Event name is required");
        return;
      }

      const ctx: TrackingEventContext = {
        ...defaultContextProvider(),
        ...contextProvider(),
      };

      const event: TrackingEvent = {
        name,
        payload,
        context: ctx,
      };

      log("trackEvent", event.name, event);

      if (adapters.size === 0) {
        // Nenhum adapter ainda — segura o evento na fila em vez de descartar.
        // Sera liberado em registerAdapter quando o primeiro adapter chegar.
        if (pendingEvents.length >= MAX_PENDING_EVENTS) {
          warn("trackEvent", `pending queue full (${MAX_PENDING_EVENTS}) — dropping "${name}". No adapter ever registered?`);
          return;
        }
        pendingEvents.push(event);
        log("trackEvent", `No adapters registered, event queued (pending: ${pendingEvents.length})`);
        return;
      }

      dispatchToAdapters(event);
    },

    registerAdapter(adapter: TrackingAdapter): void {
      if (!adapter || !adapter.name) {
        warn("registerAdapter", "Adapter must have a name");
        return;
      }
      // Captura "e novo?" ANTES do set — necessario para nao re-flushar a fila
      // numa substituicao de adapter de mesmo nome.
      const isNew = !adapters.has(adapter.name);
      if (!isNew) {
        log("registerAdapter", `Replacing existing adapter: ${adapter.name}`);
      }
      adapters.set(adapter.name, adapter);
      log("registerAdapter", `Registered: ${adapter.name}`);

      if (adapter.init) {
        try {
          adapter.init();
        } catch (err) {
          warn("registerAdapter", `init() failed for ${adapter.name}`, err);
        }
      }

      // Libera eventos que ficaram na fila (rede de seguranca). So flusha na
      // transicao real 0 -> 1 adapter novo, evitando reenvio em substituicao ou
      // quando um segundo adapter registra depois.
      if (pendingEvents.length > 0 && isNew && adapters.size === 1) {
        log("registerAdapter", `Flushing ${pendingEvents.length} pending event(s) to ${adapter.name}`);
        const queued = pendingEvents.splice(0, pendingEvents.length);
        queued.forEach((event) => dispatchToAdapters(event));
      }
    },

    getAdapters(): string[] {
      return Array.from(adapters.keys());
    },

    setContextProvider(fn: () => Partial<TrackingEventContext>): void {
      contextProvider = fn;
      log("setContextProvider", "Custom context provider set");
    },

    setDebug(enabled: boolean): void {
      service.debug = enabled;
      try {
        if (enabled) {
          localStorage.setItem(STORAGE_KEY, "true");
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch {}
      console.info(`[Tracking] Debug mode ${enabled ? "ON" : "OFF"}`);
    },

    log(label: string, ...args: unknown[]): void {
      log(label, ...args);
    },

    warn(label: string, ...args: unknown[]): void {
      warn(label, ...args);
    },

    onReady(cb: () => void): void {
      if (typeof cb !== "function") return;
      if (ready) {
        runReadyCallback(cb);
        return;
      }
      readyCallbacks.push(cb);
    },
  };

  // ─── Bootstrap síncrono e determinístico ───

  // 1. Captura o buffer do stub inline ANTES de substituir window.__tracking.
  const buffered = (window as any).__tracking?._q as Array<[string, any, boolean?]> | undefined;

  // 2. Substitui o stub pelo serviço real.
  (window as any).__tracking = service;
  log("core", "Tracking core initialized");

  // 3. Registra os adapters injetados — síncrono, então a partir daqui o
  //    registry já tem destino. NENHUM intervalo "core pronto mas sem adapter".
  initialAdapters.forEach((adapter) => service.registerAdapter(adapter));

  // 4. Drena o buffer do stub com os adapters JÁ presentes: cada evento vai
  //    direto para os destinos (nunca cai na fila pendente no caminho normal).
  if (buffered && buffered.length) {
    log("core", `Draining ${buffered.length} buffered event(s)`);
    buffered.forEach(([name, payload, productEvent]) => productEvent
      ? service.trackProductEvent(name as ProductEventName, payload)
      : service.trackEvent(name, payload));
  }

  // 5. Marca pronto e libera plugins. Como os adapters já foram registrados,
  //    `ready === true` significa de fato "adapters registrados, buffer drenado"
  //    — o contrato que onReady promete.
  ready = true;
  log(
    "core",
    `Ready. Adapters: ${service.getAdapters().join(", ") || "(none)"}. Draining ${readyCallbacks.length} onReady callback(s).`,
  );
  while (readyCallbacks.length > 0) {
    runReadyCallback(readyCallbacks.shift()!);
  }

  // 6. Evento global para consumidores que carregam mais tarde e preferem ouvir
  //    em vez de chamar onReady (ex: scripts fora deste bundle).
  window.dispatchEvent(new CustomEvent("tracking:ready"));

  return service;
}
